"use client";

import {
  type HTMLMotionProps,
  motion,
  AnimatePresence,
  animate,
  useMotionValue,
} from "framer-motion";
import Link from "next/link";
import { usePathname } from "next/navigation";
import type { ComponentProps, CSSProperties, ReactNode } from "react";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { type CentralIconName } from "@/types/central-icon";
import {
  SIDEBAR_WIDTH,
  SIDEBAR_WIDTH_ICON,
  useSidebar,
  useSidebarStore,
} from "@/stores/sidebar-store";
import useIsMobile from "@/hooks/use-is-mobile";
import { Icon } from "@/components/ui/icon";
import { ScrollArea } from "@/components/ui/scroll-area";
import { KbdShortcut } from "@/components/ui/kbd-shortcut";

/** Shared width tween for the rail + its layout gap. */
const RAIL_TRANSITION =
  "transition-[width] duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none";

export function Sidebar({
  className,
  children,
  ...props
}: ComponentProps<"div">) {
  const { state, isHovered, setIsHovered, openMobile, setOpenMobile } =
    useSidebar();
  const isMobile = useIsMobile();
  const pathname = usePathname();

  // Close the mobile drawer after in-app navigation only (not on mount, not
  // when the drawer opens). openMobile is read via getState() so the effect
  // never re-fires from the drawer opening.
  const prevPathnameRef = useRef(pathname);
  useEffect(() => {
    if (prevPathnameRef.current === pathname) return;
    prevPathnameRef.current = pathname;
    if (useSidebarStore.getState().openMobile) {
      setOpenMobile(false);
    }
  }, [pathname, setOpenMobile]);

  const showIconRail = state === "collapsed" && !isHovered;

  if (isMobile) {
    return (
      <AnimatePresence>
        {openMobile && (
          <motion.div
            key="mobile-sidebar"
            className="fixed inset-0 z-50 flex"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            <div
              className="absolute inset-0 bg-black/60"
              onClick={() => setOpenMobile(false)}
            />
            <motion.div
              data-slot="mobile-sidebar"
              className="relative flex h-full w-[min(80vw,360px)] flex-col overflow-hidden border-r border-border bg-surface-secondary"
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{
                type: "spring",
                damping: 30,
                stiffness: 400,
                mass: 0.5,
              }}
            >
              {children}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  return (
    <div
      className={cn("group peer shrink-0 text-heading", className)}
      data-state={isHovered ? "expanded" : state}
      data-collapsible={showIconRail ? "icon" : undefined}
      data-side="left"
      data-slot="sidebar"
      style={
        {
          "--sidebar-width": `${SIDEBAR_WIDTH}px`,
          "--sidebar-width-icon": `${SIDEBAR_WIDTH_ICON}px`,
        } as CSSProperties
      }
      {...props}
    >
      <div
        data-slot="sidebar-gap"
        className={cn(
          "relative shrink-0 bg-transparent",
          RAIL_TRANSITION,
          "w-[var(--sidebar-width)]",
          "group-data-[collapsible=icon]:w-[var(--sidebar-width-icon)]",
        )}
      />
      <div
        data-slot="sidebar-container"
        className={cn(
          "fixed left-0 z-10 flex w-[var(--sidebar-width)] flex-col",
          RAIL_TRANSITION,
          "top-[var(--banner-offset,0px)] h-[calc(100svh-var(--banner-offset,0px))]",
          // No divider: the sidebar shares the frame colour and the content
          // panel's own border does the separating.
          // Hover-expanded rail floats over content; give it a soft edge so it
          // reads as a layer rather than a layout shift.
          "group-data-[state=expanded]:shadow-[8px_0_24px_-16px_rgba(0,0,0,0.25)]",
          "group-data-[collapsible=icon]:w-[var(--sidebar-width-icon)]",
        )}
        onMouseEnter={() => {
          if (state === "collapsed") setIsHovered(true);
        }}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div
          data-sidebar="sidebar"
          data-slot="sidebar-inner"
          className={cn(
            "bg-surface-frame flex h-full w-full min-h-0 flex-col overflow-hidden",
            // Rows sit against the content panel's border on this side, so
            // they get a wider gutter than the window edge on the left.
            // Excluded on the rail, where icons need to stay centred.
            "group-data-[state=expanded]:pr-1",
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

/** Chrome shared by the main inset panel and any aside panel beside it. */
export const INSET_PANEL_CLASS = cn(
  "bg-surface-primary",
  "lg:overflow-hidden lg:rounded-xl lg:border lg:border-border",
  "lg:shadow-[0_1px_2px_rgba(0,0,0,0.04),0_0_0_0.5px_var(--border-subtle-alpha)]",
);

/** Where portalled aside panels land. `null` until the slot has mounted. */
const InsetAsideSlotContext = createContext<HTMLDivElement | null>(null);

/** Lets an aside announce itself so the inset can animate the slot's width. */
const InsetAsideRegistryContext = createContext<{
  register: () => void;
  unregister: () => void;
} | null>(null);

/** Same feel as the sidebar rail's width tween — the two should move alike. */
const ASIDE_SPRING = { type: "spring", stiffness: 420, damping: 40, mass: 0.7 } as const;
/** Gutter between the main panel and the aside, matching `lg:gap-2`. */
const ASIDE_GAP = 8;

/**
 * Everything to the right of the sidebar. On desktop the content sits in a
 * bordered, rounded panel inset from the top / right / bottom edges (Linear
 * style) so the sidebar reads as part of the window frame rather than a
 * column of the page. On mobile it stays full-bleed.
 *
 * A page can render a second panel beside the main one — the AI assistant
 * on Talent Search and Sequences — by wrapping it in `SidebarInsetAside`.
 * It's portalled into a slot after the main panel so it sits as a sibling
 * rectangle with its own border, rather than a column clipped inside.
 *
 * Arriving on a page with an aside is animated: the slot's gutter springs
 * open while the panel grows in from zero. Leaving is not — the panel
 * unmounts with the page and the main panel takes the space at once.
 */
export function SidebarInset({
  className,
  children,
  ...props
}: ComponentProps<"div">) {
  const [asideSlot, setAsideSlot] = useState<HTMLDivElement | null>(null);
  const [asideCount, setAsideCount] = useState(0);
  const hasAside = asideCount > 0;
  // Below lg the inset has no gutters, so neither should the aside.
  const isMobile = useIsMobile();

  const marginLeft = useMotionValue(0);

  const registry = useMemo(
    () => ({
      register: () => setAsideCount((n) => n + 1),
      unregister: () => setAsideCount((n) => Math.max(0, n - 1)),
    }),
    [],
  );

  useEffect(() => {
    if (!hasAside) {
      marginLeft.jump(0);
      return;
    }
    // The panel animates its own width in from zero; the gutter opens with it.
    const gap = animate(marginLeft, isMobile ? 0 : ASIDE_GAP, ASIDE_SPRING);
    return () => gap.stop();
  }, [hasAside, isMobile, marginLeft]);

  return (
    <InsetAsideRegistryContext.Provider value={registry}>
      <InsetAsideSlotContext.Provider value={asideSlot}>
        <div
          data-slot="sidebar-inset"
          className={cn(
            "relative flex min-h-0 min-w-0 flex-1 flex-row",
            "lg:py-2 lg:pr-2",
            className,
          )}
          {...props}
        >
          <div
            data-slot="sidebar-inset-panel"
            className={cn(
              "flex min-h-0 min-w-0 flex-1 flex-col",
              INSET_PANEL_CLASS,
            )}
          >
            {children}
          </div>
          <motion.div
            ref={setAsideSlot}
            data-slot="sidebar-inset-aside"
            data-state={hasAside ? "open" : "closed"}
            className="flex min-h-0 shrink-0"
            style={{ marginLeft }}
          />
        </div>
      </InsetAsideSlotContext.Provider>
    </InsetAsideRegistryContext.Provider>
  );
}

/**
 * Render `children` as a panel beside the main inset panel. Falls back to
 * rendering in place when there's no `SidebarInset` above (tests, previews),
 * so callers don't have to special-case it.
 */
export function SidebarInsetAside({ children }: { children: ReactNode }) {
  const registry = useContext(InsetAsideRegistryContext);
  const slot = useContext(InsetAsideSlotContext);

  useEffect(() => {
    if (!registry) return;
    registry.register();
    return registry.unregister;
  }, [registry]);

  if (!registry) return <>{children}</>;
  // Inside an inset but the slot hasn't mounted yet: wait one commit rather
  // than flashing the panel inline and then moving it.
  if (!slot) return null;
  return createPortal(children, slot);
}

export function SidebarHeader({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="sidebar-header"
      className={cn("flex h-14 shrink-0 flex-col gap-2", className)}
      {...props}
    />
  );
}

/**
 * Masks the bottom edge of a scroll viewport while more content sits below,
 * so the last row fades out instead of looking clipped. A mask rather than a
 * gradient overlay: the sidebar and the mobile drawer sit on different
 * surface colours, and a mask doesn't need to know either.
 */
export function useBottomScrollFade() {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [canScrollDown, setCanScrollDown] = useState(false);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const update = () => {
      const { scrollTop, scrollHeight, clientHeight } = viewport;
      setCanScrollDown(scrollHeight - scrollTop - clientHeight > 1);
    };

    viewport.addEventListener("scroll", update, { passive: true });
    // Rows stream in as searches load and the rail collapses/expands, so
    // watch both boxes. The observer's initial callback seeds the state.
    const observer = new ResizeObserver(update);
    observer.observe(viewport);
    if (viewport.firstElementChild) observer.observe(viewport.firstElementChild);

    return () => {
      viewport.removeEventListener("scroll", update);
      observer.disconnect();
    };
  }, []);

  return {
    viewportRef,
    fadeClassName: canScrollDown
      ? "[mask-image:linear-gradient(to_bottom,#000_calc(100%-2rem),transparent)]"
      : undefined,
  };
}

/**
 * Scrolling body of the sidebar. Desktop scopes its scrolling to the searches
 * list instead (see `SidebarSearchesSection`); this backs the mobile drawer,
 * where the whole panel scrolls as one.
 */
export function SidebarContent({ className, children }: ComponentProps<"div">) {
  const { viewportRef, fadeClassName } = useBottomScrollFade();

  return (
    <ScrollArea
      data-slot="sidebar-content"
      className={cn("flex min-h-0 flex-1 flex-col", className)}
      viewportRef={viewportRef}
      viewportClassName={cn("flex flex-col", fadeClassName)}
      contentClassName="flex w-full min-w-0 flex-col"
      constrainContentWidth
    >
      {children}
    </ScrollArea>
  );
}

export function SidebarFooter({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="sidebar-footer"
      className={cn("flex shrink-0 flex-col", className)}
      {...props}
    />
  );
}

export function SidebarGroup({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="sidebar-group"
      className={cn("relative flex w-full min-w-0 flex-col", className)}
      {...props}
    />
  );
}

export function SidebarGroupLabel({
  className,
  ...props
}: HTMLMotionProps<"div">) {
  return (
    <motion.div
      data-slot="sidebar-group-label"
      className={cn(
        "flex h-7 shrink-0 items-end px-2 pb-1 text-[11px] font-medium uppercase tracking-[0.06em] text-contrast/35",
        "group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:pb-0",
        className,
      )}
      variants={{
        hidden: { opacity: 0, y: -4 },
        visible: {
          opacity: 1,
          y: 0,
          transition: { duration: 0.25 },
        },
      }}
      {...props}
    />
  );
}

export function SidebarGroupContent({
  className,
  ...props
}: ComponentProps<"div">) {
  return (
    <div
      data-slot="sidebar-group-content"
      className={cn("w-full text-sm", className)}
      {...props}
    />
  );
}

/**
 * Hairline between sidebar sections. Full-bleed of the padded column when
 * expanded; shrinks to a short centered tick on the icon rail so the rhythm
 * survives collapse.
 */
export function SidebarSectionRule({
  expanded,
  className,
}: {
  expanded: boolean;
  className?: string;
}) {
  return (
    <div
      role="separator"
      aria-orientation="horizontal"
      className={cn(
        "flex h-px shrink-0 items-center",
        expanded ? "mx-3.5" : "justify-center",
        className,
      )}
    >
      <div
        className={cn(
          "h-px bg-[var(--border-subtle-alpha)] transition-[width] duration-200",
          expanded ? "w-full" : "w-4",
        )}
      />
    </div>
  );
}

const sectionDividerTransition = {
  duration: 0.15,
  ease: [0.25, 0.1, 0.25, 1] as const,
};

/** @deprecated Prefer `SidebarSectionRule` / `SidebarGroupLabel`. Kept for the mobile drill panels. */
export function SidebarSectionDivider({
  label,
  expanded,
}: {
  label: string;
  expanded: boolean;
}) {
  return (
    <div className="relative flex h-5 items-center px-1.5">
      <motion.div
        animate={{
          width: expanded ? 0 : 20,
          opacity: expanded ? 0 : 1,
        }}
        transition={sectionDividerTransition}
        className="absolute left-1.5 h-px bg-contrast/10"
      />
      <motion.span
        animate={{
          opacity: expanded ? 1 : 0,
          x: expanded ? 0 : -4,
        }}
        transition={{ ...sectionDividerTransition, delay: expanded ? 0.05 : 0 }}
        className="whitespace-nowrap text-xs font-medium leading-4 text-contrast/40"
      >
        {label}
      </motion.span>
    </div>
  );
}

export function SidebarMenu({ className, ...props }: HTMLMotionProps<"ul">) {
  return (
    <motion.ul
      data-slot="sidebar-menu"
      className={cn("flex w-full min-w-0 flex-col gap-px", className)}
      initial="hidden"
      animate="visible"
      variants={{
        hidden: { opacity: 0 },
        visible: {
          opacity: 1,
          transition: {
            staggerChildren: 0.04,
            delayChildren: 0.02,
          },
        },
      }}
      {...props}
    />
  );
}

export function SidebarMenuItem({
  className,
  ...props
}: HTMLMotionProps<"li">) {
  return (
    <motion.li
      data-slot="sidebar-menu-item"
      className={cn("relative", className)}
      variants={{
        hidden: { opacity: 0, y: -3 },
        visible: {
          opacity: 1,
          y: 0,
          transition: { duration: 0.22, ease: [0.22, 1, 0.36, 1] },
        },
      }}
      {...props}
    />
  );
}

/** Count pill (notifications etc.) — warm brand tint, tabular digits. */
export function SidebarBadge({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      data-slot="sidebar-badge"
      className={cn(
        "flex h-[18px] min-w-[18px] shrink-0 items-center justify-center rounded-full px-1.5",
        "bg-ai/12 text-[10.5px] font-semibold leading-none tabular-nums",
        "text-[#b45309] dark:text-ai",
        "shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--ai)_18%,transparent)]",
        className,
      )}
    >
      {children}
    </span>
  );
}

/** Muted keyboard hint rendered as text (e.g. "⌘K"), not keycaps. */
export function SidebarShortcut({
  keys,
  className,
}: {
  /** Use "mod" for ⌘ / Ctrl. */
  keys: string[];
  className?: string;
}) {
  // One keycap per key (⌘ K / Ctrl K), the same treatment the command
  // palette uses for its hints.
  return (
    <span
      data-slot="sidebar-shortcut"
      aria-hidden
      // A flex box, not an inline span: otherwise the caps sit on the text
      // baseline and ride high against the label.
      className={cn("flex shrink-0 items-center", className)}
    >
      <KbdShortcut
        keys={keys}
        size="sm"
        className="[&>kbd]:transition-colors [&>kbd]:group-hover/item:text-contrast/50 [&>kbd]:group-hover/quick:text-contrast/50"
      />
    </span>
  );
}

interface SidebarItemContentProps {
  icon: CentralIconName;
  label: string;
  badge?: number | string;
  dot?: boolean;
  onDotClick?: (e: React.MouseEvent) => void;
  expanded: boolean;
  active?: boolean;
  disabled?: boolean;
  chevron?: boolean;
  /** Keyboard hint shown at the trailing edge, e.g. `["mod", "K"]`. */
  shortcut?: string[];
  /** Arbitrary trailing slot (rendered after badge/shortcut). */
  trailing?: ReactNode;
}

export function SidebarItemContent({
  icon,
  label,
  badge,
  dot,
  onDotClick,
  expanded,
  active,
  disabled,
  chevron,
  shortcut,
  trailing,
}: SidebarItemContentProps) {
  return (
    <div
      className={cn(
        "group/item relative flex h-8 items-center rounded-md transition-colors duration-150",
        expanded ? "w-full gap-2 px-2" : "w-8 justify-center",
        disabled
          ? "text-contrast/35"
          : active
            ? "sidebar-active text-heading"
            : "text-contrast/65 hover:bg-contrast/4 hover:text-heading",
      )}
      aria-disabled={disabled ? true : undefined}
      title={!disabled && !expanded ? label : undefined}
    >
      <Icon
        name={icon}
        size={16}
        className={cn(
          "size-4 shrink-0 transition-colors duration-150",
          disabled
            ? "text-contrast/25"
            : active
              ? "text-heading"
              : "text-contrast/45 group-hover/item:text-heading",
        )}
      />
      {expanded && (
        <>
          <span className="flex min-w-0 flex-1 items-center gap-1.5">
            <span className="truncate text-[13px] font-medium leading-5">
              {label}
            </span>
            {dot && (
              <span
                role={onDotClick ? "button" : undefined}
                tabIndex={onDotClick ? 0 : undefined}
                onClick={onDotClick}
                onKeyDown={
                  onDotClick
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onDotClick(e as unknown as React.MouseEvent);
                        }
                      }
                    : undefined
                }
                className={cn(
                  "size-1.5 shrink-0 rounded-full bg-ai",
                  onDotClick && "cursor-pointer hover:ring-2 hover:ring-ai/30",
                )}
              />
            )}
          </span>
          {badge !== undefined && <SidebarBadge>{badge}</SidebarBadge>}
          {shortcut && <SidebarShortcut keys={shortcut} />}
          {trailing}
          {chevron && (
            <Icon
              name="IconChevronRight"
              size={14}
              className="shrink-0 text-contrast/35"
            />
          )}
        </>
      )}
    </div>
  );
}

interface SidebarItemProps extends SidebarItemContentProps {
  href?: string;
  external?: boolean;
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  disabledReason?: string;
}

export function SidebarItem({
  href,
  icon,
  label,
  badge,
  dot,
  onDotClick,
  external,
  expanded,
  onClick,
  onMouseEnter,
  onMouseLeave,
  active: activeOverride,
  disabled,
  disabledReason,
  chevron,
  shortcut,
  trailing,
  ...rest
}: SidebarItemProps) {
  const pathname = usePathname();
  const active =
    activeOverride ??
    (href ? pathname === href || pathname.startsWith(href + "/") : false);

  const content = (
    <SidebarItemContent
      icon={icon}
      label={label}
      badge={badge}
      dot={dot}
      onDotClick={onDotClick}
      expanded={expanded}
      active={active}
      disabled={disabled}
      chevron={chevron}
      shortcut={shortcut}
      trailing={trailing}
    />
  );

  const focusRing =
    "rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ai/40";
  // On the icon rail the visible label is gone; keep the name for AT.
  const railLabel = expanded ? undefined : label;

  if (disabled) {
    const disabledTooltip =
      disabledReason ?? `${label} is not available right now`;

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="w-full cursor-not-allowed select-none" {...rest}>
            {content}
          </div>
        </TooltipTrigger>
        <TooltipContent side="right" align="center" className="max-w-xs">
          {disabledTooltip}
        </TooltipContent>
      </Tooltip>
    );
  }

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        aria-label={railLabel}
        className={cn("w-full cursor-pointer", focusRing)}
        {...rest}
      >
        {content}
      </button>
    );
  }

  if (external) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={railLabel}
        className={cn("block", focusRing)}
      >
        {content}
      </a>
    );
  }

  return (
    <Link href={href!} aria-label={railLabel} className={cn("block", focusRing)}>
      {content}
    </Link>
  );
}
